import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Award,
  Clock,
  Gift,
  ShieldCheck,
  Sparkles,
  BadgeCheck,
  Users,
  Rocket,
  Check,
  Wallet,
  PackageOpen,
  IndianRupee,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { getFoundingCount } from "@/lib/founding.functions";
import { FoundingCheckoutForm } from "@/components/founding/FoundingCheckoutForm";
import { FoundingWall } from "@/components/founding/FoundingWall";
import heroMag__asset from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
const heroMag = heroMag__asset.url;
import heroLiver__asset from "@/assets/liver-biome-v2/01-hero.webp.asset.json";
const heroLiver = heroLiver__asset.url;
import heroOva__asset from "@/assets/product-hero/ova-balance-hero.png.asset.json";
const heroOva = heroOva__asset.url;


export const Route = createFileRoute("/founding-99")({
  component: Founding99Page,
  head: () => ({
    meta: [
      { title: "TruWe Founding 99. Charter Membership · ₹199 Access" },
      {
        name: "description",
        content:
          "Reserve your spot with ₹199 Founding Access, fully redeemable at launch. Only 99 charter members. 7-day early access, complimentary samples, digital badge and priority support.",
      },
      { property: "og:title", content: "TruWe Founding 99, ₹199 Charter Access" },
      {
        property: "og:description",
        content:
          "Only 99 charter members. ₹199 Founding Access, fully redeemable at launch. Early access to every drop, complimentary samples, digital badge.",
      },
      { property: "og:url", content: "https://truwe.in/founding-99" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/founding-99" }],
  }),
});

// Launch target — 60 days out from today's project date
const LAUNCH_ISO = "2026-09-15T10:00:00+05:30";

const BENEFITS = [
  {
    icon: IndianRupee,
    title: "₹199 Launch Credit",
    body: "Your Founding Access fee returns as a ₹199 credit, automatically applied at launch checkout.",
  },
  {
    icon: Clock,
    title: "7-Day Early Product Access",
    body: "Every new TruWe formula unlocks in your account 7 days before public launch.",
  },
  {
    icon: Gift,
    title: "Complimentary Launch Samples",
    body: "Free samples of eligible upcoming products with your launch order.",
  },
  {
    icon: BadgeCheck,
    title: "Digital Founding 99 Badge",
    body: "A permanent digital badge and signed certificate with your Member number.",
  },
  {
    icon: ShieldCheck,
    title: "Priority Support",
    body: "A dedicated human queue, always first in line, always answered.",
  },
  {
    icon: Sparkles,
    title: "Charter-Only Drops",
    body: "First dibs on limited-edition launches and Founding-only bundles.",
  },
];

const STEPS = [
  {
    step: "01",
    icon: Wallet,
    title: "Pay ₹199",
    body: "Reserve one of the 99 charter seats with a simple ₹199 Founding Access fee.",
  },
  {
    step: "02",
    icon: Award,
    title: "Get Member Access",
    body: "Receive your Founding Member number, digital badge and signed certificate within 24 hours.",
  },
  {
    step: "03",
    icon: PackageOpen,
    title: "Redeem at Launch",
    body: "Your ₹199 returns as a launch credit, apply it to any TruWe product at launch.",
  },
];

const PRODUCTS = [
  {
    handle: "mag-absorb-pro",
    name: "Mag Absorb Pro",
    tag: "Sleep · Nerves · Recovery",
    image: heroMag,
    accent: "from-brand/15 via-white to-brand-soft/40",
  },
  {
    handle: "liver-biome-plus",
    name: "Liver Biome Plus",
    tag: "Detox · Gut · Liver Axis",
    image: heroLiver,
    accent: "from-brand-soft/60 via-white to-brand/10",
  },
  {
    handle: "ova-balance",
    name: "Ova Balance",
    tag: "PCOS · Cycle · Hormones",
    image: heroOva,
    accent: "from-white via-brand-soft/50 to-brand/10",
  },
];


const FAQ = [
  {
    q: "Is my ₹199 refundable at launch?",
    a: "Yes, your ₹199 Founding Access is fully redeemable as a launch credit. It's automatically applied to your first order when TruWe goes live.",
  },
  {
    q: "What happens if I don't order at launch?",
    a: "Your ₹199 credit stays in your account for 12 months from launch. Use it on any product, any time within that window.",
  },
  {
    q: "How many memberships are there?",
    a: "Exactly 99. Once filled, the program closes permanently, no second batch.",
  },
  {
    q: "What does 7-day early access mean?",
    a: "Every new TruWe SKU is unlocked in your account 7 days before public launch, with a private purchase link.",
  },
  {
    q: "How do complimentary samples work?",
    a: "When we launch a new product, eligible Founding Members receive a complimentary sample bundled with their next order, subject to availability.",
  },
  {
    q: "Is the membership transferable?",
    a: "No, it's tied to your email and account. Your Founding Member number is permanent to you.",
  },
  {
    q: "How and when do I get the digital badge?",
    a: "Your Founding 99 badge and certificate are emailed within 24 hours of reserving your spot, and appear on your account profile.",
  },
];

function Founding99Page() {
  return (
    <div className="bg-white pb-24 md:pb-0">
      <HeroSection />
      <RedeemBand />
      <StepsSection />
      <BenefitsGrid />
      <FoundingWall />
      <ProductsPreview />
      <ScarcitySection />
      <FaqSection />
      <StickyMobileCta />
    </div>
  );
}

/* ---------------- HERO ---------------- */

function HeroSection() {
  return (
    <section className="relative overflow-hidden border-b border-border/60 bg-gradient-to-br from-brand-soft/70 via-white to-brand-soft/30">
      <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-brand-deep/10 blur-3xl" />

      <div className="container relative grid gap-12 py-16 md:grid-cols-[1.1fr_1fr] md:py-24">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-brand/25 bg-white/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand backdrop-blur">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand" />
            </span>
            Founding 99 · Charter Membership
          </span>

          <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] text-brand-deep md:text-6xl">
            Become one of TruWe's
            <br />
            first <span className="text-brand">99 Founding Members.</span>
          </h1>
          <div className="mt-6 h-[3px] w-14 rounded-full bg-brand" />
          <p className="mt-5 max-w-xl text-base leading-relaxed text-brand-deep/75 md:text-lg">
            Reserve your spot with <strong className="font-semibold text-brand-deep">₹199 Founding Access</strong> fully redeemable at launch. Early access, complimentary samples and a permanent Founding number that's yours for life.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12px] font-medium text-brand-deep/70">
            <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-brand" /> ₹199 redeemable at launch</span>
            <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-brand" /> 7-day early access</span>
            <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-brand" /> Digital badge + certificate</span>
          </div>
        </div>

        <div className="relative">
          <LiveMembershipCard />
        </div>
      </div>
    </section>
  );
}

/* ---------------- LIVE CARD ---------------- */

function LiveMembershipCard() {
  const { data } = useQuery({
    queryKey: ["founding-count"],
    queryFn: () => getFoundingCount(),
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
  const claimed = data?.claimed ?? 0;
  const total = data?.total ?? 99;
  const remaining = data?.remaining ?? 99;
  const pct = Math.min(100, (claimed / total) * 100);

  return (
    <div className="relative">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-brand/15 via-transparent to-brand-deep/15 blur-2xl" />
      <div className="relative rounded-3xl border border-brand/15 bg-white/85 p-6 shadow-luxury-lg backdrop-blur md:p-8">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-soft px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand" />
            Founding 99 · Live
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-deep/60">
            Charter
          </span>
        </div>

        <div className="mt-6 flex items-baseline justify-between">
          <span className="font-display text-5xl font-bold text-brand-deep tabular-nums">
            {claimed}
            <span className="text-brand-deep/40"> / {total}</span>
          </span>
          <span className="text-[12px] font-medium text-brand-deep/70">Claimed</span>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-brand-soft">
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand to-brand-deep transition-[width] duration-700 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-2 text-[12px] text-brand-deep/70">
          <strong className="font-semibold text-brand-deep">{remaining}</strong> spots remaining
        </p>


        <div className="mt-6">
          <FoundingBadgeMock />
        </div>

        <div id="founding-reserve" className="mt-6">
          <FoundingCheckoutForm />
        </div>
      </div>
    </div>
  );
}

/* ---------------- COUNTDOWN ---------------- */

function useCountdownTo(iso: string) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const target = new Date(iso).getTime();
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s };
}

function CountdownStrip() {
  const { d, h, m, s } = useCountdownTo(LAUNCH_ISO);
  const cells: Array<[string, number]> = [
    ["Days", d],
    ["Hrs", h],
    ["Min", m],
    ["Sec", s],
  ];
  return (
    <div className="rounded-2xl border border-brand/15 bg-brand-soft/40 p-3">
      <p className="text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">
        Launch countdown
      </p>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {cells.map(([label, val]) => (
          <div key={label} className="rounded-xl bg-white px-1 py-2 text-center shadow-sm">
            <div className="font-display text-xl font-bold text-brand-deep tabular-nums">
              {String(val).padStart(2, "0")}
            </div>
            <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-brand-deep/60">
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- BADGE MOCK ---------------- */

function FoundingBadgeMock({ memberNumber }: { memberNumber?: number }) {
  const num = String(memberNumber ?? 1).padStart(3, "0");
  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand-deep/20 bg-gradient-to-br from-brand-deep via-brand-deep to-brand p-5 text-white shadow-luxury-lg">
      <div className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-white/5 blur-2xl" />

      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-[0.28em] text-white/70">
            TruWe Founding 99
          </p>
          <p className="mt-1 font-display text-xl font-bold tracking-tight">
            Charter Member
          </p>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-white/10 backdrop-blur">
          <Award className="h-5 w-5" />
        </div>
      </div>
      <div className="relative mt-6 flex items-end justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/60">
            Member
          </p>
          <p className="font-display text-2xl font-bold tabular-nums">#{num}</p>
        </div>
        <p className="text-right text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70">
          Future
          <br />
          Nutrition
        </p>
      </div>
    </div>
  );
}

/* ---------------- REDEEM BAND ---------------- */

function RedeemBand() {
  return (
    <section className="border-b border-border/60 bg-white">
      <div className="container py-14 md:py-20">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
            The ₹199 promise
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-brand-deep md:text-5xl">
            Your ₹199 is not spent. <span className="text-brand">It comes back at launch.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-brand-deep/75">
            Think of Founding Access as a placeholder, you're holding a seat, not buying a discount. On launch day, ₹199 lands back in your account as credit, ready to apply to any TruWe product.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- STEPS ---------------- */

function StepsSection() {
  return (
    <section className="border-b border-border/60 bg-brand-soft/30">
      <div className="container py-14 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">How it works</p>
          <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-brand-deep md:text-4xl">
            Three steps. One permanent seat.
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {STEPS.map(({ step, title, body, icon: Icon }) => (
            <div
              key={step}
              className="group relative rounded-3xl border border-border/70 bg-white p-7 transition hover:-translate-y-1 hover:border-brand/25 hover:shadow-luxury"
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-4xl font-bold text-brand/30">{step}</span>
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-soft text-brand transition group-hover:bg-brand group-hover:text-white">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-brand-deep">{title}</h3>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-brand-deep/75">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- BENEFITS ---------------- */

function BenefitsGrid() {
  return (
    <section className="border-b border-border/60 bg-white">
      <div className="container py-14 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">Membership benefits</p>
          <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-brand-deep md:text-4xl">
            Everything included in Founding 99
          </h2>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-3xl border border-border/70 bg-white p-6 transition hover:-translate-y-1 hover:border-brand/25 hover:shadow-luxury"
            >
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-soft text-brand">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-brand-deep">{title}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-brand-deep/70">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- PRODUCTS ---------------- */

function ProductsPreview() {
  return (
    <section className="border-b border-border/60 bg-brand-soft/30">
      <div className="container py-14 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">Launching soon</p>
          <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-brand-deep md:text-4xl">
            What you'll get early access to
          </h2>
          <p className="mt-4 text-[14px] leading-relaxed text-brand-deep/70">
            Three clinical formulations, engineered around bioavailability. Founding Members see them 7 days before anyone else.
          </p>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {PRODUCTS.map((p) => (
            <div
              key={p.handle}
              className={`group overflow-hidden rounded-3xl border border-border/70 bg-gradient-to-br ${p.accent} p-6 transition hover:-translate-y-1 hover:border-brand/25 hover:shadow-luxury`}
            >
              <div className="flex h-40 items-center justify-center overflow-hidden rounded-2xl border border-white bg-white/70 backdrop-blur">
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="h-full w-full object-contain p-3 transition duration-500 group-hover:scale-[1.04]"
                />
              </div>
              <div className="mt-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand">
                  Founding early access
                </p>
                <h3 className="mt-2 font-display text-xl font-semibold text-brand-deep">{p.name}</h3>
                <p className="mt-1 text-[13px] text-brand-deep/70">{p.tag}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SCARCITY ---------------- */

function ScarcitySection() {
  return (
    <section className="relative overflow-hidden bg-brand-deep py-16 text-white md:py-24">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[80%] -translate-x-1/2 rounded-full bg-brand/25 blur-3xl" />
      <div className="container relative mx-auto max-w-3xl text-center">
        <Rocket className="mx-auto h-10 w-10 opacity-90" />
        <h2 className="mt-4 font-display text-3xl font-bold leading-tight md:text-5xl">
          Only 99 spots.
          <br />
          <span className="text-white/70">No second batch.</span>
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-white/75 md:text-base">
          When the 99th seat is claimed, the program closes permanently. There is no waitlist upgrade path, no future re-open, just the 99 people who moved first.
        </p>

        <a
          href="#founding-reserve"
          className="mx-auto mt-8 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-8 text-sm font-semibold text-brand-deep shadow-lg transition hover:bg-white/90"
        >
          Reserve My Spot, ₹199 <ArrowRight className="h-4 w-4" />
        </a>
        <div className="mx-auto mt-6 inline-flex max-w-[90%] flex-wrap items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-center text-[12px] uppercase tracking-[0.14em] text-white/80">
          <Users className="h-4 w-4 shrink-0" /> ₹199 · Fully redeemable at launch
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */

function FaqSection() {
  return (
    <section className="border-t border-border/60 bg-white">
      <div className="container py-14 md:py-20">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">FAQ</p>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-brand-deep md:text-4xl">
              Answers for prospective members
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-8">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-brand/15">
                <AccordionTrigger className="text-left font-display text-base font-semibold text-brand-deep">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[13.5px] leading-relaxed text-brand-deep/75">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

/* ---------------- STICKY MOBILE CTA ---------------- */

function StickyMobileCta() {
  const scrollToForm = () => {
    const el = document.getElementById("founding-reserve");
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      const input = el?.querySelector<HTMLInputElement>('input[type="text"], input:not([type])');
      input?.focus();
    }, 500);
  };
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-white/95 px-4 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-3 backdrop-blur md:hidden">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-brand-deep">
            Founding 99 Access · ₹199
          </p>
          <p className="truncate text-[11px] text-brand-deep/65">
            ₹199 redeemable at launch
          </p>
        </div>
        <Button
          onClick={scrollToForm}
          size="lg"
          className="h-11 shrink-0 rounded-full bg-brand px-5 text-[13px] font-semibold text-white hover:bg-brand/90"
        >
          Reserve Spot <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ---------------- RESERVE FORM ---------------- */

// (checkout flow lives in <FoundingCheckoutForm /> — see @/components/founding/FoundingCheckoutForm)
